import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Database,
  HardDrive,
  Download,
  Upload,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  FileArchive,
  Server,
  ShieldCheck,
  Cloud,
  Code2,
  Image,
  LockKeyhole
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../components/ui/alert-dialog';

export default function SystemAdmin() {
  const [systemInfo, setSystemInfo] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [resetPreview, setResetPreview] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  
  // Modal states
  const [showResetModal, setShowResetModal] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  
  // Form states
  const [resetType, setResetType] = useState('transactional');
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [restoreConfirmation, setRestoreConfirmation] = useState('');
  const [backupName, setBackupName] = useState('');
  const [backupDescription, setBackupDescription] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false);
  const [catalogSeedStatus, setCatalogSeedStatus] = useState(null);
  const [catalogSeedLoading, setCatalogSeedLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [infoResult, backupsResult, catalogSeedResult] = await Promise.allSettled([
        api.get('/admin/system-info'),
        api.get('/admin/backups'),
        api.get('/admin/storefront-catalog-seed')
      ]);
      if (infoResult.status === 'rejected') throw infoResult.reason;
      if (backupsResult.status === 'rejected') throw backupsResult.reason;
      setSystemInfo(infoResult.value.data);
      setBackups(backupsResult.value.data);
      if (catalogSeedResult.status === 'fulfilled') {
        setCatalogSeedStatus(catalogSeedResult.value.data);
      } else {
        setCatalogSeedStatus({ active: false, complete: false, counts: {} });
        console.error('Failed to load storefront test catalogue status', catalogSeedResult.reason);
      }
    } catch (error) {
      toast.error('Failed to fetch system information');
    } finally {
      setLoading(false);
    }
  };

  const fetchResetPreview = async (type) => {
    try {
      const response = await api.get('/admin/data-reset/preview', {
        params: { reset_type: type }
      });
      setResetPreview(response.data);
    } catch (error) {
      toast.error('Failed to fetch reset preview');
    }
  };

  const handleResetTypeChange = (type) => {
    setResetType(type);
    fetchResetPreview(type);
  };

  const handleDataReset = async () => {
    if (resetConfirmation !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }

    try {
      const response = await api.post('/admin/data-reset', {
        reset_type: resetType,
        confirmation_code: resetConfirmation,
        reason: 'Requested from ERP System Administration'
      });
      toast.success(response.data.message);
      setShowResetModal(false);
      setResetConfirmation('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    }
  };

  const handleCreateCatalogSeed = async () => {
    const confirmed = window.confirm(
      'Create the removable SEED catalogue in the live ERP database? Test products will become visible on the storefront.'
    );
    if (!confirmed) return;

    try {
      setCatalogSeedLoading(true);
      const response = await api.post('/admin/storefront-catalog-seed', {
        confirmation_code: 'SEED'
      });
      setCatalogSeedStatus(response.data);
      toast.success(response.data.message || 'Storefront test catalogue created');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create storefront test catalogue');
    } finally {
      setCatalogSeedLoading(false);
    }
  };

  const handleRemoveCatalogSeed = async () => {
    const confirmed = window.confirm(
      'Remove every product, variation, category, brand, collection and Cloudinary image created by the SEED catalogue? Real catalogue data will remain unchanged.'
    );
    if (!confirmed) return;

    try {
      setCatalogSeedLoading(true);
      const response = await api.post('/admin/storefront-catalog-seed/cleanup', {
        confirmation_code: 'REMOVE SEED'
      });
      setCatalogSeedStatus(response.data);
      toast.success(response.data.message || 'Storefront test catalogue removed');
      if (response.data.cloudinary_warning) {
        toast.warning(`Database records were removed, but Cloudinary reported: ${response.data.cloudinary_warning}`);
      }
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove storefront test catalogue');
    } finally {
      setCatalogSeedLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      const response = await api.post('/admin/backups', {
        name: backupName || undefined,
        description: backupDescription || undefined,
        backup_type: 'full'
      });
      toast.success(`Full ERP + storefront backup created: ${response.data.total_records?.toLocaleString() || 0} records`);
      setShowBackupModal(false);
      setBackupName('');
      setBackupDescription('');
      await fetchData();
      await handleDownloadBackup(response.data, false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backup failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const fetchRestorePreview = async (backupId) => {
    try {
      setRestorePreview(null);
      setRestorePreviewLoading(true);
      const response = await api.get(`/admin/restore/preview/${backupId}`);
      setRestorePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify this backup');
    } finally {
      setRestorePreviewLoading(false);
    }
  };

  const handleRestore = async () => {
    if (restoreConfirmation !== 'RESTORE') {
      toast.error('Please type RESTORE to confirm');
      return;
    }

    try {
      setRestoreLoading(true);
      const response = await api.post('/admin/restore', {
        backup_id: selectedBackup.id,
        confirmation_code: restoreConfirmation
      });
      toast.success(response.data.message);
      setShowRestoreModal(false);
      setRestoreConfirmation('');
      setSelectedBackup(null);
      setRestorePreview(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;
    try {
      await api.delete(`/admin/backups/${backupId}`);
      toast.success('Backup deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const handleDownloadBackup = async (backup, showPreparingToast = true) => {
    try {
      if (showPreparingToast) toast.info('Preparing verified backup download...');
      const response = await api.get(`/admin/backups/${backup.id}/download`, {
        responseType: 'blob'
      });

      const disposition = response.headers['content-disposition'] || '';
      const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const plainFilename = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const fallbackName = `${String(backup.name || 'urbnsw-backup').replace(/[^a-z0-9._-]+/gi, '-')}.urbnsw-backup.zip`;
      const filename = encodedFilename
        ? decodeURIComponent(encodedFilename)
        : (plainFilename || backup.download_filename || fallbackName);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup saved in Atlas and downloaded for off-site storage');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backup exists, but the download failed');
    }
  };

  const handleUploadBackup = async () => {
    if (!uploadFile) {
      toast.error('Please select a backup file');
      return;
    }

    try {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await api.post('/admin/backups/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Backup uploaded successfully! ${response.data.total_records} records in ${response.data.collections.length} collections.`);
      setShowUploadModal(false);
      setUploadFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload backup');
    } finally {
      setUploadLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    const value = Number(bytes) || 0;
    if (value <= 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(value) / Math.log(k)), sizes.length - 1);
    return parseFloat((value / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-admin-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">System Administration</h1>
          <p className="text-slate-500 mt-1">Protect ERP, storefront and company database data</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Records</CardTitle>
            <Database className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{systemInfo?.total_records?.toLocaleString()}</div>
            <p className="text-xs text-slate-500 mt-1">Across all collections</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Backups</CardTitle>
            <HardDrive className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{systemInfo?.backup_count}</div>
            <p className="text-xs text-slate-500 mt-1">
              {formatBytes(systemInfo?.backup_storage_used_bytes || 0)} used
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Last Backup</CardTitle>
            <Clock className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {systemInfo?.latest_backup 
                ? formatDate(systemInfo.latest_backup.created_at)
                : 'No backups yet'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Persistent Storage</CardTitle>
            <Cloud className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-semibold">MongoDB Atlas</div>
            <p className="text-xs text-slate-500 mt-1">Survives Render restarts and redeploys</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-900">
            <ShieldCheck className="w-5 h-5" />
            Full application-data protection
          </CardTitle>
          <CardDescription>
            One company-scoped archive protects the shared MongoDB data used by both applications.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 text-sm">
            <div className="rounded-lg border bg-white p-3">
              <Database className="w-5 h-5 text-emerald-600 mb-2" />
              <p className="font-semibold">MongoDB data</p>
              <p className="text-slate-500 mt-1">All detected company collections, BSON types and indexes.</p>
            </div>
            <div className="rounded-lg border bg-white p-3">
              <Server className="w-5 h-5 text-emerald-600 mb-2" />
              <p className="font-semibold">ERP + storefront</p>
              <p className="text-slate-500 mt-1">Products, variations, orders, finance, customers and settings.</p>
            </div>
            <div className="rounded-lg border bg-white p-3">
              <Image className="w-5 h-5 text-amber-600 mb-2" />
              <p className="font-semibold">Cloudinary references</p>
              <p className="text-slate-500 mt-1">Image URLs and public IDs are included; image files stay in Cloudinary.</p>
            </div>
            <div className="rounded-lg border bg-white p-3">
              <Code2 className="w-5 h-5 text-blue-600 mb-2" />
              <p className="font-semibold">Source code</p>
              <p className="text-slate-500 mt-1">ERP and storefront code remains protected by your Git repositories.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Collection Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5" />
            Collection Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {systemInfo?.collection_stats && Object.entries(systemInfo.collection_stats).map(([name, count]) => (
              <div key={name} className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 capitalize">{name.replace(/_/g, ' ')}</p>
                <p className="text-xl font-bold">{count}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-violet-200 bg-violet-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-violet-900">
            <Database className="w-5 h-5" />
            Online Storefront Test Catalogue
          </CardTitle>
          <CardDescription>
            Create or remove isolated test catalogue data directly in the live DigitalOcean backend.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-violet-200 bg-white p-4 text-sm text-slate-700">
            <p>
              Creates 2 brands, 1 main category, 2 subcategories, 2 collections,
              2 simple products, 2 variable products and 10 Color/Size variations.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Every record starts with SEED and is ownership-marked. Re-running is safe and removal never targets real catalogue records.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {['brands', 'categories', 'collections', 'products', 'variations'].map((key) => (
              <div key={key} className="rounded-lg border bg-white p-3">
                <p className="text-xs capitalize text-slate-500">{key}</p>
                <p className="text-xl font-bold text-slate-900">
                  {catalogSeedStatus?.counts?.[key] || 0}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleCreateCatalogSeed}
              disabled={catalogSeedLoading}
              data-testid="create-storefront-seed-btn"
            >
              {catalogSeedLoading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              {catalogSeedStatus?.active ? 'Refresh Test Catalogue' : 'Create Test Catalogue'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveCatalogSeed}
              disabled={catalogSeedLoading || !catalogSeedStatus?.active}
              data-testid="remove-storefront-seed-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Test Catalogue
            </Button>
            <span className={`text-xs font-medium ${catalogSeedStatus?.complete ? 'text-emerald-700' : 'text-slate-500'}`}>
              {catalogSeedStatus?.complete
                ? 'Complete test catalogue is active'
                : catalogSeedStatus?.active
                  ? 'Partial test catalogue detected — refresh to repair it'
                  : 'No test catalogue is active'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Backup Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileArchive className="w-5 h-5 text-blue-600" />
              Backup & Restore
            </CardTitle>
            <CardDescription>Create persistent, verified recovery points and off-site downloads</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={() => setShowBackupModal(true)} data-testid="create-backup-btn">
                <HardDrive className="w-4 h-4 mr-2" />
                Create Full Backup
              </Button>
              <Button variant="outline" onClick={() => setShowUploadModal(true)} data-testid="upload-backup-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Backup
              </Button>
            </div>

            {/* Backup List */}
            <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
              {backups.length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <FileArchive className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No backups yet</p>
                </div>
              ) : (
                backups.map((backup) => (
                  <div key={backup.id} className="p-3 hover:bg-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {backup.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : backup.status === 'failed' ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{backup.name}</p>
                          {backup.is_uploaded && (
                            <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                              Uploaded
                            </span>
                          )}
                          {backup.integrity_verified ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                              <ShieldCheck className="w-3 h-3" /> Verified
                            </span>
                          ) : backup.status === 'completed' ? (
                            <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                              Legacy
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-slate-500">
                          {formatDate(backup.created_at)} • {formatBytes(backup.file_size || 0)}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {(backup.total_records || 0).toLocaleString()} records • {backup.collection_count || backup.collections_backed_up?.length || 0} collections
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {backup.status === 'completed' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadBackup(backup)}
                            data-testid={`download-backup-${backup.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedBackup(backup);
                              setRestorePreview(null);
                              setRestoreConfirmation('');
                              fetchRestorePreview(backup.id);
                              setShowRestoreModal(true);
                            }}
                            data-testid={`restore-backup-${backup.id}`}
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBackup(backup.id)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-backup-${backup.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Data Reset Section */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Data Reset
            </CardTitle>
            <CardDescription>Reset system data with safety controls</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> Data reset operations cannot be undone. 
                A backup will be created automatically before reset.
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Reset Options:</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <strong>Transactional Reset:</strong> Clears orders, payments, journal entries. Keeps products, customers, accounts.</li>
                <li>• <strong>Full Reset:</strong> Clears all ERP and storefront business data and non-admin users. Keeps admin users, audit history, backups, and the essential company identity record.</li>
              </ul>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => {
                setShowResetModal(true);
                fetchResetPreview('transactional');
              }}
              data-testid="open-reset-modal-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Reset Data
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Create Backup Modal */}
      <Dialog open={showBackupModal} onOpenChange={setShowBackupModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create ERP + Storefront Backup</DialogTitle>
            <DialogDescription>
              Create a persistent recovery point in MongoDB Atlas and download an off-site copy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <p className="font-semibold flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Included automatically
              </p>
              <p className="mt-1 text-emerald-800">
                Company settings, ERP records, storefront products/customers/orders, finance data,
                users, email settings, Cloudinary links, BSON types and collection indexes.
              </p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Cloudinary image files and Git source code are stored by their own services and are not duplicated inside this database archive.
              Render environment secrets are intentionally excluded; keep a separate secure copy of them.
            </div>
            <div>
              <label className="text-sm font-medium">Backup Name (Optional)</label>
              <Input
                value={backupName}
                onChange={(e) => setBackupName(e.target.value)}
                placeholder="e.g., Before Year End"
                data-testid="backup-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description (Optional)</label>
              <Input
                value={backupDescription}
                onChange={(e) => setBackupDescription(e.target.value)}
                placeholder="e.g., Backup before financial year close"
                data-testid="backup-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackupModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBackup} disabled={backupLoading} data-testid="confirm-backup-btn">
              {backupLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating & verifying...
                </>
              ) : (
                <>
                  <HardDrive className="w-4 h-4 mr-2" />
                  Create & Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Reset Modal */}
      <AlertDialog open={showResetModal} onOpenChange={setShowResetModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Data Reset Confirmation
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Reset Type</label>
                  <Select value={resetType} onValueChange={handleResetTypeChange}>
                    <SelectTrigger data-testid="reset-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transactional">Transactional Reset</SelectItem>
                      <SelectItem value="full">Full Reset</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {resetPreview && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <span className="font-medium text-sm mb-2 block">Data to be cleared:</span>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(resetPreview.collections).map(([name, count]) => (
                        <div key={name} className="flex justify-between">
                          <span className="capitalize">{name.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                    {resetPreview.warnings?.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="font-medium text-sm text-orange-600 block">Warnings:</span>
                        <ul className="text-sm text-orange-600 mt-1">
                          {resetPreview.warnings.map((w, i) => (
                            <li key={i}>• {w}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <span className="text-sm text-red-700 font-medium block">
                    Type <strong>RESET</strong> to confirm:
                  </span>
                  <Input
                    value={resetConfirmation}
                    onChange={(e) => setResetConfirmation(e.target.value)}
                    className="mt-2"
                    placeholder="Type RESET"
                    data-testid="reset-confirmation-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setResetConfirmation('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDataReset}
              disabled={resetConfirmation !== 'RESET'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reset-btn"
            >
              Reset Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Modal */}
      <AlertDialog open={showRestoreModal} onOpenChange={setShowRestoreModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore from Backup</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {selectedBackup && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <span className="font-medium block">{selectedBackup.name}</span>
                    <span className="text-sm text-slate-600 block">
                      Created: {formatDate(selectedBackup.created_at)}
                    </span>
                  </div>
                )}

                {restorePreviewLoading && (
                  <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Verifying archive checksums and ownership...
                  </div>
                )}

                {restorePreview && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="font-medium text-sm">Data to be restored</span>
                      {restorePreview.integrity_verified && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                          <ShieldCheck className="w-4 h-4" /> Integrity verified
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm max-h-52 overflow-y-auto pr-1">
                      {Object.entries(restorePreview.collections).map(([name, count]) => (
                        <div key={name} className="flex justify-between">
                          <span className="capitalize">{name.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 border-t pt-3 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                      <span>{restorePreview.total_records?.toLocaleString()} records</span>
                      <span>{Object.keys(restorePreview.collections).length} collections</span>
                      <span>Schema v{restorePreview.schema_version}</span>
                    </div>
                  </div>
                )}

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <span className="text-sm text-orange-700">
                    <strong>Warning:</strong> This will replace your current data with the backup data.
                    A verified recovery backup is created automatically before any data changes.
                  </span>
                </div>

                <div>
                  <span className="text-sm font-medium block">
                    Type <strong>RESTORE</strong> to confirm:
                  </span>
                  <Input
                    value={restoreConfirmation}
                    onChange={(e) => setRestoreConfirmation(e.target.value)}
                    className="mt-2"
                    placeholder="Type RESTORE"
                    data-testid="restore-confirmation-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setRestoreConfirmation('');
              setSelectedBackup(null);
              setRestorePreview(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoreConfirmation !== 'RESTORE' || !restorePreview?.integrity_verified || restoreLoading}
              data-testid="confirm-restore-btn"
            >
              {restoreLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Restoring atomically...</>
              ) : (
                <><LockKeyhole className="w-4 h-4 mr-2" /> Restore Verified Backup</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Backup Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Upload Backup File
            </DialogTitle>
            <DialogDescription>
              Upload a previously downloaded URBNSW backup. Ownership and checksums are verified before it is saved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".zip,.json,.gz"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="hidden"
                id="backup-file-input"
                data-testid="backup-file-input"
              />
              <label htmlFor="backup-file-input" className="cursor-pointer">
                <FileArchive className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                {uploadFile ? (
                  <div>
                    <p className="font-medium text-slate-900">{uploadFile.name}</p>
                    <p className="text-sm text-slate-500">{formatBytes(uploadFile.size)}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-slate-600">Click to select a backup file</p>
                    <p className="text-sm text-slate-400 mt-1">Preferred: .urbnsw-backup.zip</p>
                  </div>
                )}
              </label>
            </div>
            {uploadFile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  The backend will reject corrupt files, modified collection checksums, unsafe collection names,
                  and backups belonging to another company.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowUploadModal(false);
                setUploadFile(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUploadBackup} 
              disabled={!uploadFile || uploadLoading}
              data-testid="confirm-upload-btn"
            >
              {uploadLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Backup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
